import React, { useState, useEffect } from 'react';
import { Edit3, Save, X } from 'lucide-react';
import { getBlogIntro, saveBlogIntro } from '../../services/homepageContentService';

const BlogIntro = ({ isAdmin, setMessage }) => {
  const introBoxClass =
    'mb-4 animate-fade-in rounded-r-xl bg-card border border-line border-l-4 border-l-indigo-700 px-4 py-3 sm:px-5 sm:py-4 shadow-[0_1px_2px_rgba(28,25,23,0.04)]';

  const IntroLabel = ({ children }) => (
    <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-indigo-800 mb-1.5">
      {children}
    </p>
  );

  const IntroBody = ({ content }) => (
    <div className="prose max-w-none text-sm text-gray-800 leading-relaxed">
      {content.split('\n').map((paragraph, index) => (
        <p key={index} className={index > 0 ? 'mt-2' : ''}>{paragraph}</p>
      ))}
    </div>
  );
  const [isEditing, setIsEditing] = useState(false);
  const [intro, setIntro] = useState({
    title: 'Journal',
    content: 'Welcome to my journal! Here I share my thoughts on development, technology, and the projects I\'m working on — including living docs that evolve over time.'
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBlogIntro = async () => {
      try {
        const data = await getBlogIntro();
        if (data) {
          setIntro(data);
        }
      } catch (error) {
        console.error('Error loading blog intro:', error);
      } finally {
        setLoading(false);
      }
    };
    loadBlogIntro();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      await saveBlogIntro(intro);
      setMessage("Journal intro updated successfully!");
      setIsEditing(false);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving blog intro:', error);
      setMessage("Failed to save journal intro. Please try again.");
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reload from database to discard changes
    const loadBlogIntro = async () => {
      try {
        const data = await getBlogIntro();
        if (data) {
          setIntro(data);
        }
      } catch (error) {
        console.error('Error reloading blog intro:', error);
      }
    };
    loadBlogIntro();
  };

  const handleInputChange = (field, value) => {
    setIntro(prev => ({ ...prev, [field]: value }));
  };

  if (loading && !intro.content) {
    return (
      <div className={introBoxClass}>
        <div className="animate-pulse">
          <div className="h-3 bg-gray-200 rounded w-20 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-full mb-1.5"></div>
          <div className="h-3 bg-gray-200 rounded w-5/6"></div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className={introBoxClass}>
        <IntroLabel>{intro.title}</IntroLabel>
        <IntroBody content={intro.content} />
      </div>
    );
  }

  return (
    <div className={introBoxClass}>
      {!isEditing && (
        <div className="flex justify-end -mt-0.5 mb-1">
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-1 px-3 py-1 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition"
          >
            <Edit3 className="w-4 h-4" />
            <span>Edit Intro</span>
          </button>
        </div>
      )}

      {isEditing ? (
        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={intro.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              className="field"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
            <textarea
              value={intro.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              rows="4"
              className="field"
              placeholder="Enter intro content..."
            />
          </div>

          <div className="flex space-x-3">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
            <button
              onClick={handleCancel}
              className="flex items-center space-x-1 px-4 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition"
            >
              <X className="w-4 h-4" />
              <span>Cancel</span>
            </button>
          </div>
        </div>
      ) : (
        <>
          <IntroLabel>{intro.title}</IntroLabel>
          <IntroBody content={intro.content} />
        </>
      )}
    </div>
  );
};

export default BlogIntro;
